"use client";

import React, { useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useRouter } from "next/navigation";

import { Login } from "@/actions/auth";
import { FormButton } from "@/app/components/form/form-button";
import { notify } from "@/app/components/toast";
import { Picture } from "@/app/components/form/picture";
import data from "/data/data.json";
import build from "/data/build.json";
import Link from "next/link";
import Password from "@/app/components/form/password";
import Input from "@/app/components/form/input";
import { LoginSchema } from "@/schema/auth";

export default function Page() {
  const router = useRouter();
  const initialValues = { username: "", password: "" };

  // our own, authoritative loading flag
  const redirectingRef = useRef(false);
  const [redirecting, setRedirecting] = useState(false);

  return (
    <div className="d-flex justify-content-center align-items-center position-relative"
         style={{ height: "100vh", width: "100vw", background: `url('/img/bg.avif') no-repeat`, backgroundSize: "cover" }}>
      <div className="card" style={{ width: "30rem", paddingTop: "3rem" }}>
        <Picture
          path={`/img/${data[process.env.NEXT_PUBLIC_APP_NAME].logo}`}
          height={75}
          alt="logo"
        />
        <center style={{ paddingTop: "1rem" , paddingBottom: "1rem"}}>
          <h3>Sign In</h3>
          <p>Get access to your account</p>
        </center>

        <div className="card-body">
          <Formik
            initialValues={initialValues}
            validationSchema={LoginSchema}
            onSubmit={async (values, actions) => {
              // show spinner immediately and keep it until navigation or real error
              redirectingRef.current = true;
              setRedirecting(true);

              try {
                const res = await Login(values); // may throw NEXT_REDIRECT on success

                // success-with-payload path (no redirect throw)
                const ok =
                  res?.meta?.code === 200 || res?.ok === true || res?.success === true;

                if (ok) {
                  const redirectTo =
                    res?.redirect ||
                    res?.data?.redirect ||
                    res?.data?.redirect_to ||
                    "/";

                  await Promise.resolve(); // let "Logging in…" paint before navigation
                  router.replace(redirectTo);
                  return; // do NOT clear loading; page will unmount
                }

                // failure payload → stop spinner
                redirectingRef.current = false;
                setRedirecting(false);
                actions.setSubmitting(false);
                notify(
                  res || {
                    meta: { code: 422, message: "Invalid username or password" },
                  }
                );
              } catch (err) {
                const digest = String(err?.digest || "");
                const msg = String(err?.message || "");

                // success-with-redirect (server action threw NEXT_REDIRECT)
                if (digest === "NEXT_REDIRECT" || digest.startsWith("NEXT_REDIRECT") || msg.includes("NEXT_REDIRECT")) {
                  return; // keep spinner; page will navigate & unmount
                }

                // real error → stop spinner
                redirectingRef.current = false;
                setRedirecting(false);
                actions.setSubmitting(false);
                notify({
                  meta: {
                    code: err?.code ?? 500,
                    message: msg || "Something went wrong. Please try again.",
                  },
                });
              }
              // no finally: we never clear submitting/loading in the redirect path
            }}
          >
            {() => (
              <Form noValidate>
                <Input name="username" label="Username/Email" required col={12} />
                <Password name="password" label="Password" col={12} required />

                <div className="text-end">
                  <Link href="/forgot-password">Forgot Password?</Link>
                </div>

                <div className="py-4 text-start">
                  <FormButton
                    label={redirecting ? "Logging in…" : "Login"}
                    className="w-50"
                    loading={redirecting}              // ← drives spinner/disabled
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <small className="text-muted position-absolute" style={{ bottom: "10px", right: "15px", fontSize: "0.75rem" }}>
        {build?.commit ? <>Build {build.commit} · {build.branch} · {build.time}</> : <>Build info unavailable</>}
      </small>
    </div>
  );
}